import { createContext, useEffect, useState, useRef } from 'react';
import { connectToServer } from '../lib/serverModel';
import { useMLPerformance } from 'hooks/MLPerformanceHook';
import { useSubscription } from 'hooks/SubscriptionHook';
import { useWebsiteCompatibility } from './WebsiteCompatibilityHook';
import { createHook } from 'utils/utils';
import { toast } from 'react-toastify';
import { translations } from 'i18n';
import { sendToAnalyticsCustomEventObj } from 'lib/googleAnalytics';
import { useBandwidth } from './BandwidthHooks';
import { useConnectedDevices } from './ConnectedDevicesHook';
import {
  getActualCcWsServerUrl,
  getRegistrationToken,
  getAccessToken,
  getActualOauthServerUrl,
} from 'api/oauth-api';

const toBase64 = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const OAUTH_SERVERS = [
  { name: 'US', value: 'https://oauth-us.feelme.com' },
  { name: 'EU', value: 'https://oauth-eu.feelme.com' },
  { name: 'AUS', value: 'https://oauth-aus.feelme.com' },
];

const oauthServerApiUrl = process.env.REACT_APP_OAUTH_SERVER_API_URL;
if (oauthServerApiUrl) {
  let parsedServers;
  try {
    parsedServers = JSON.parse(oauthServerApiUrl);
  } catch (error) {
    console.error('Incorrect or missing parameter:', error);
  }

  for (const [name, value] of Object.entries(parsedServers)) {
    OAUTH_SERVERS.unshift({ name, value });
  }
}

export const WS_SERVERS = [
  { name: 'US', value: 'https://mlserver.feelme.com' },
  { name: 'EU', value: 'https://mlserver-eu.feelme.com' },
  { name: 'AUS', value: 'https://mlserver-aus.feelme.com' },
];
const ccWsServerApiUrl = process.env.REACT_APP_ADDITIONAL_ML_SERVERS;
if (ccWsServerApiUrl) {
  for (const [name, value] of Object.entries(ccWsServerApiUrl)) {
    WS_SERVERS.unshift({ name, value });
  }
}

const AUTOPILOT_MODE_STORAGE_KEY = 'ml-autopilot-mode';
const TEST_MODE_STORAGE_KEY = 'ml-test-mode';
const ON_VALUE = 'on';
const PAUSE_BETWEEN_CONNECTION_AND_DISCOVERING_DEVICES_MS = 500;

const PredictionContext = createContext();

export const usePrediction = () => createHook('usePrediction', PredictionContext);

export const PredictionContextProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  // Value predicted by the network
  const [predict, setPredict] = useState(0);
  // Current frame number
  const [frameNo, setFrameNo] = useState(0);
  const [frameData, setFrameData] = useState([]);
  const [frameUrl, setFrameUrl] = useState('');
  const { onFrame, onMLServerMessage } = useMLPerformance();
  const [serverUrl, setServerUrl] = useState(getActualCcWsServerUrl());
  const [oauthServerUrl, setOauthServerUrl] = useState(getActualOauthServerUrl());
  const [autopilot, setAutopilot] = useState(
    localStorage.getItem(AUTOPILOT_MODE_STORAGE_KEY) === ON_VALUE,
  );
  const [testSignal, setTestSignal] = useState(
    localStorage.getItem(TEST_MODE_STORAGE_KEY) === ON_VALUE,
  );
  const [isPauseFrameSending, setPauseFrameSending] = useState(false);
  const { tokenObj, accessToken, setAccessToken, setRegistrationToken } = useSubscription();
  const { token } = tokenObj || {};

  const { logSentSize } = useBandwidth();

  const { checkCompatibility } = useWebsiteCompatibility();
  const [isCompatible, setIsCompatible] = useState(false);
  const [isConnectedToMLServer, setIsConnectedToMLServer] = useState(false);

  const toastId = useRef(null);

  const { getConnectedDevices } = useConnectedDevices();
  const connectedDevices = getConnectedDevices();

  useEffect(() => {
    if (!oauthServerUrl) {
      return;
    }
    const setTokens = async () => {
      const [registrationToken, webSocketServerUrl] = await getRegistrationToken(oauthServerUrl);
      setRegistrationToken(registrationToken);
      setServerUrl(webSocketServerUrl);

      const accessToken = await getAccessToken(oauthServerUrl);
      setAccessToken(accessToken);
    };

    setTokens();
  }, [oauthServerUrl]);

  useEffect(() => {
    setIsCompatible(checkCompatibility(frameUrl));
  }, [frameUrl, checkCompatibility]);

  useEffect(() => {
    // If autopilot mode is ON, we keep 'on' value in the AUTOPILOT_MODE_STORAGE_KEY key.
    // If autopilot mode is OFF, we remove the key. This is the default value.
    if (autopilot) {
      localStorage.setItem(AUTOPILOT_MODE_STORAGE_KEY, ON_VALUE);
    } else {
      localStorage.removeItem(AUTOPILOT_MODE_STORAGE_KEY);
    }
  }, [autopilot]);

  useEffect(() => {
    // If test mode is ON, we keep 'on' value in the TEST_MODE_STORAGE_KEY key.
    // If test mode is OFF, we remove the key. This is the default value.
    if (testSignal) {
      localStorage.setItem(TEST_MODE_STORAGE_KEY, ON_VALUE);
    } else {
      localStorage.removeItem(TEST_MODE_STORAGE_KEY);
    }
  }, [testSignal]);

  useEffect(() => {
    if (!serverUrl || !token) {
      return;
    }

    const start = async () => {
      setIsConnectedToMLServer(false);
      const serverConnection = connectToServer(serverUrl, token, accessToken);
      setConnection(serverConnection);

      serverConnection.onConnected(() => {
        setIsConnectedToMLServer(true);

        toast.dismiss(toastId.current);
        toastId.current = null;

        setTimeout(() => {
          serverConnection.requestStatusOfDevices();
        }, PAUSE_BETWEEN_CONNECTION_AND_DISCOVERING_DEVICES_MS);
      });

      serverConnection.subscribeConnectionError(() => {
        setIsConnectedToMLServer(false);
        // display notification only once for each disconnection period,
        // until successful reconnection or user dismisses it
        if (!toastId.current) {
          toastId.current = toast.info(translations('Error.ReconnectingToServer'), {
            autoClose: false,
          });
        }
      });

      /**
       * Process a video frame
       * @param {obj} payload - message payload, containing the frame and its dimentions
       */
      const handleFrameData = async (payload) => {
        if (!payload?.webp) {
          return;
        }

        const { index, webp, url } = payload;
        setFrameNo(index);
        setFrameUrl(url);
        setFrameData(webp);
        onFrame(index, performance.now());

        // Stop function and history recording when an unsupported website is detected.
        if (!isCompatible) {
          return;
        }

        const msg = {
          frameIndex: index,
          image: toBase64(webp),
          autopilot: autopilot ? 1 : 0,
        };
        const msgSize = JSON.stringify(msg).length;
        logSentSize(index, msgSize);

        if (!isPauseFrameSending) {
          // Test frames do not use the resources of the ML server and can be processed even if there are no connected devices.
          if (testSignal) {
            serverConnection.requestTestServerPrediction(msg);
          } else {
            if (connectedDevices.length) {
              const ccWsServerUrl = getActualCcWsServerUrl();
              const isNewMlServer = ccWsServerUrl == serverUrl;
              serverConnection.requestServerPrediction(msg, isNewMlServer);
            }
          }
        }
      };

      let prevIdx = 0;
      const unsubscribeFromPredictions = serverConnection.subscribeServerPredictions(
        ({ frameIndex, engine_position: percent }) => {
          if (prevIdx === frameIndex) {
            // This frame can be ignored
            // Could be caused by video FPS lower than 30.
            return;
          }
          prevIdx = frameIndex;
          onMLServerMessage(frameIndex, performance.now());
          setPredict(percent);
          const { pos, speed } = window.keonState || {};
          const timestamp = Date.now();
          // TODO fix it. It crashes after adding short strokes funtionality
          // updateHistoryWithPrediction(frameIndex, frameUrl, percent, speed, pos, timestamp);
        },
      );

      const listener = (event) => {
        // On every message from the Chrome extension content script
        // We only accept messages from ourselves
        if (event.source !== window) {
          return;
        }
        const { data } = event;
        const { payload, what } = data;
        if (!payload || what !== 'frame') {
          return;
        }
        handleFrameData(payload);
      };
      window.addEventListener('message', listener, false);

      return () => {
        // Return cleanup function
        unsubscribeFromPredictions();
        window.removeEventListener('message', listener);
        serverConnection.closeConnection();
      };
    };

    const initializationPromise = start();
    return async () => {
      const cleanup = await initializationPromise;
      cleanup();
    };
  }, [
    serverUrl,
    token,
    accessToken,
    frameUrl,
    autopilot,
    testSignal,
    isCompatible,
    connectedDevices.length,
    isPauseFrameSending,
  ]);

  useEffect(() => {
    sendToAnalyticsCustomEventObj('website_connected', { hostname: frameUrl });
  }, [frameUrl]);

  return (
    <PredictionContext.Provider
      value={{
        predict,
        frameNo,
        frameData,
        frameUrl,
        serverUrl,
        setServerUrl,
        testSignal,
        setTestSignal,
        autopilot,
        setAutopilot,
        isCompatible,
        connection,
        isConnectedToMLServer,
        oauthServerUrl,
        setOauthServerUrl,
        setPauseFrameSending,
      }}
    >
      {children}
    </PredictionContext.Provider>
  );
};
